import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fixDate } from "../../helpers/helperFunctions";
import NewButton from "../Buttons/NewButton";
import SubscriptionModal from "../Modals/SubscriptionModal";
import toast from "react-hot-toast";
import { icons } from "../../helpers/constants";
import SearchbarNew from "../Searchbar/SearchbarNew";
import { FLAVOR } from "../../helpers/constants";

const DeviceStatuses = ({ devices, deactivatePlan, undoCancel, undoSchedule, provider }) => {
	const { t } = useTranslation();
	const [activeDevices, setActiveDevices] = useState([]);
	const [inactiveDevices, setInactiveDevices] = useState([]);
	const [failedDevices, setFailedDevices] = useState([]);
	const [activeDevicesFull, setActiveDevicesFull] = useState([]);
	const [inactiveDevicesFull, setInactiveDevicesFull] = useState([]);
	const [failedDevicesFull, setFailedDevicesFull] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [selectedId, setSelectedId] = useState(null);

	useEffect(() => {
		if (!Array.isArray(devices)) return;

		const active = [];
		const inactive = [];
		const failed = [];

		for (const d of devices) {
			if (d?.user_action_required === true) failed.push(d);
			else if (d?.dataplan_status === "active" || d?.dataplan_status === "ending" || d?.dataplan_status === "plan_changing" || d?.dataplan_status === "duration_changing") active.push(d);
			else if (d?.dataplan_status === "inactive") inactive.push(d);
		}

		setActiveDevices(active);
		setInactiveDevices(inactive);
		setFailedDevices(failed);
		setActiveDevicesFull(active);
		setInactiveDevicesFull(inactive);
		setFailedDevicesFull(failed);
	}, [devices]);

	const sortedActive = [...activeDevices].sort((a, b) => {
		// 1. Active first
		const aActive = a?.dataplan_status === "active";
		const bActive = b?.dataplan_status === "active";

		if (aActive !== bActive) {
			return aActive ? -1 : 1;
		}

		// 2. Then sort by name
		return (a?.name || "").localeCompare(b?.name || "", undefined, {
			sensitivity: "base",
		});
	});

	const openModal = (deviceid) => {
		setSelectedId(deviceid)
		setShowModal(true)
	}
	const closeModal = () => {
		setShowModal(false)
		setSelectedId(null)
	}

	const cancelPlan = async() => {
		if(!selectedId) return;

		  try {
			
				await deactivatePlan(selectedId);

				closeModal();
			} catch (err) {
				toast.error(err?.message || "Something went wrong");
			} finally {
		}
	}

	const filterSearch = (searchWord) => {
		const term = (searchWord ?? "").trim().toLowerCase();

		if (!term) {
			setActiveDevices(activeDevicesFull);
			setFailedDevices(failedDevicesFull);
			setInactiveDevices(inactiveDevicesFull);
			return;
		}

		const matches = (device) => {
			const name = (device?.name ?? "").toLowerCase();
			const uid = (device?.imei ?? "").toLowerCase();
			const vin = (device?.vin_number ?? "").toLowerCase();
			return name.includes(term) || uid.includes(term) || vin.includes(term);
		};

		setActiveDevices((activeDevicesFull ?? []).filter(matches));
		setFailedDevices((failedDevicesFull ?? []).filter(matches));
		setInactiveDevices((inactiveDevicesFull ?? []).filter(matches));
	};

	return (
		<>
			<div className="d-flex flex-column align-items-center">
				<div className={`d-flex flex-column w-100 dataplan-selection`}>
					<div className="w-50 mb-3">
						<SearchbarNew submitSearch={(data) => filterSearch(data)} />
					</div>
					<p className="bold mb-1">{t("subscription.active_devices")}</p>
					<div className="d-flex flex-column">
						<div className={`settings-radius`}>
							{sortedActive?.map((device, index) => {
								const provider = device?.payment_provider;
								const duration = device?.dataplan?.duration;
								const plan_id = device?.dataplan?.plan_id
								const next_payment = device?.dataplan?.duration_end;
								const ending = device?.dataplan_status === "ending"
								const planned_change = device?.dataplan?.has_scheduled_change
								const id_number = FLAVOR === "bike" ? device?.vin_number : device?.imei

								const notadminToast = () => toast(t("subscription.not_admin"), { icon: <img className="size_20 grey-blue-icon" src={icons.info_icon} alt="" />, autoClose: 10000 });
								const durationLabel = (duration) => {
									if (!duration) return;
									switch (Number(duration)) {
										case 1:
											return t("subscription.monthly");
										case 12:
											return t("subscription.yearly");
										default:
											return t("subscription.duration_months", { count: duration });
									}
								};
								return (
									<div key={device?.id} className={`position-relative p-3 w-100 d-flex bg-white justify-content-between ${index < sortedActive?.length - 1 ? "border-settings" : ""}`}>
										<div className="d-flex flex-column overflow-hidden">
											<h4 className="mb-1 text-truncate">{`${device.name}`}</h4>
											{ending &&
												<p className="bold red">
													{t("subscription.subscription_cancelled")}
												</p>
											}
											<p className="">
												{t("subscription.id")}: <span className="bold">{id_number}</span>
											</p>
											<p className="">
												{t("subscription.data_plan")}:{" "}
												<span className="bold">
													{device?.dataplan?.plan_name} - {durationLabel(duration)}
												</span>
											</p>
											{planned_change &&
											<p className="">
												{t("subscription.upcoming_data_plan")}:{" "}
												<span className="bold">
													{device?.dataplan?.next_plan_name} - {durationLabel(device?.dataplan?.next_plan_duration)}
												</span>
											</p>
											}

											<p className="">
												{t("subscription.payment")}: <span className="bold">{provider === "stripe" ? t("subscription.card") : t("subscription.invoice")}</span>
											</p>
											{next_payment && (
												<p className="">
													{ending ? t("subscription.subscription_ends") : t("subscription.next_payment")}: <span className="bold">{fixDate(next_payment, true)}</span>
												</p>
											)}
										</div>
										{provider !== "stripe"
										? null
										: !device?.is_admin
										? <img className="cursor-pointer primary-icon position-absolute bottom-0 end-0 mb-3 me-3" src={icons.info_icon} alt={""} onClick={() => notadminToast()} />
										: planned_change
										?
											<NewButton className={`bg-primary white-white p bold  py-2 px-3 rounded24 position-absolute bottom-0 end-0 mb-3 me-3`} onClick={() => undoSchedule(device?.id, duration, plan_id)}>
												{t("subscription.undo_schedule")}
											</NewButton>		
										: ending 
											?
											<NewButton className={`bg-gradient white-white p bold py-2 px-3 rounded24 position-absolute bottom-0 end-0 mb-3 me-3`} onClick={() => undoCancel(device?.id)}>
												{t("subscription.keep_dataplan")}
											</NewButton>										
											:
											(
											<NewButton className={`bg-red white-white p bold py-2 px-3 rounded24 position-absolute bottom-0 end-0 mb-3 me-3`} onClick={() => openModal(device?.id)}>
												{t("subscription.cancel_dataplan")}
											</NewButton>
											)
										}

									</div>
								);
							})}
						</div>
						{sortedActive?.length === 0 && (
							<div className="d-flex flex-column settings-radius bg-white p-3 w-100">
								<span className="regular">{t("settings.no_devices")}</span>
							</div>
						)}
					</div>
					{failedDevices?.length > 0 && (
						<>
							<p className="bold mt-3 mb-1">{t("subscription.failed_devices")}</p>
							<div className="d-flex flex-column">
								<div className={`settings-radius`}>
									{failedDevices?.map((device, index) => {
										const provider = device?.payment_provider;
										const duration = device?.dataplan?.duration;
										const date = device?.dataplan?.duration_end;
										const status = device?.dataplan_status
										const id_number = FLAVOR === "bike" ? device?.vin_number : device?.imei
										const durationLabel = (duration) => {
											if (!duration) return t("subscription.no_data");
											switch (Number(duration)) {
												case 1:
													return t("subscription.monthly");
												case 12:
													return t("subscription.yearly");
												default:
													return t("subscription.duration_months", { count: Number(duration) });
											}
										};
										return (
											<div key={index} className={`p-3 w-100 d-flex flex-column bg-white ${index < failedDevices?.length - 1 ? "border-settings" : ""}`}>
												<div className="d-flex flex-column overflow-hidden">
													<h4 className="mb-1 text-truncate">{device.name}</h4>
                                                    {status === "pending_activation"
                                                        ? (
															<>
															<p className="bold red">{t(`subscription.${status}`)}</p>		
															<p className="">
																{t("subscription.id")}: <span className="bold">{id_number}</span>
															</p>
															<p className="">
																{t("subscription.data_plan")}:{" "}
																<span className="bold">
																	{device?.dataplan?.plan_name} - {durationLabel(duration)}
																</span>
															</p>
															<p className="">
																{t("subscription.payment")}: <span className="bold">{provider === "stripe" ? t("subscription.card") : t("subscription.invoice")}</span>
															</p>
															</>
                                                        )
                                                        : (
                                                            <>
															<p className="">
																{t("subscription.id")}: <span className="bold">{id_number}</span>
															</p>
                                                            <p className="">
                                                                {t("subscription.previous_data_plan")}:{" "}
                                                                <span className="bold">
                                                                    {device?.dataplan?.plan_name} {durationLabel(duration)}
                                                                </span>
                                                            </p>
                                                            {provider !== "none" && (
                                                                <p className="">
                                                                    {t("subscription.previous_payment")}: <span className="bold">{provider === "stripe" ? t("subscription.card") : t("subscription.invoice")}</span>
                                                                </p>
                                                            )}
                                                            {date && (
                                                                <p className="text-red">
                                                                    {t("subscription.cancelled")}: <span className="bold">{fixDate(date)}</span>
                                                                </p>
                                                            )}
                                                            </>
                                                        )
                                                    }

												</div>
											</div>
										);
									})}
								</div>
							</div>
						</>
					)}
					<p className="bold mb-1 mt-3">{t("subscription.inactive_devices")}</p>
					<div className="d-flex flex-column">
						<div className={`settings-radius`}>
							{inactiveDevices?.map((device, index) => {
								const provider = device?.payment_provider;
								const duration = device?.dataplan?.duration;
								const date = device?.dataplan?.duration_end;
								const id_number = FLAVOR === "bike" ? device?.vin_number : device?.imei
								const durationLabel = (duration) => {
									if (!duration) return t("subscription.no_data");
									switch (Number(duration)) {
										case 1:
											return t("subscription.monthly");
										case 12:
											return t("subscription.yearly");
										default:
											return t("subscription.duration_months", { count: Number(duration) });
									}
								};
								return (
									<div key={index} className={`p-3 w-100 d-flex flex-column bg-white ${index < inactiveDevices?.length - 1 ? "border-settings" : ""}`}>
										<div className="d-flex flex-column overflow-hidden">
											<h4 className="mb-1 text-truncate">{device.name}</h4>
											<p className="">
												{t("subscription.id")}: <span className="bold">{id_number}</span>
											</p>
											<p className="">
												{t("subscription.previous_data_plan")}:{" "}
												<span className="bold">
													{device?.dataplan?.plan_name} {durationLabel(duration)}
												</span>
											</p>
											{provider !== "none" && (
												<p className="">
													{t("subscription.previous_payment")}: <span className="bold">{provider === "stripe" ? t("subscription.card") : t("subscription.invoice")}</span>
												</p>
											)}
											{date && (
												<p className="text-red">
													{t("subscription.cancelled")}: <span className="bold">{fixDate(date)}</span>
												</p>
											)}
										</div>
									</div>
								);
							})}
						</div>
						{inactiveDevices?.length === 0 && (
							<div className="d-flex flex-column settings-radius bg-white p-3 w-100">
								<span className="regular">{t("settings.no_devices")}</span>
							</div>
						)}
					</div>
				</div>
			</div>
			<SubscriptionModal show={showModal} type={"deactivate"} cancelPlan={cancelPlan} onClose={() => closeModal()} />

		</>
	);
};

export default DeviceStatuses;
